<template>
  <div />
</template>

<script>

/**
 * Vue: Nosto api call (nosto-api-call)
 * -----------------------------------------------------------------------------
 * Component to make Nosto API calls.
 * https://docs.nosto.com/techdocs/implementing-nosto/implement-search/implement-search-using-api/implementing-search-page
 *
 * @param {String} choices - Formatted filter choices to use in API call.
 * @param {Number} collectionId - Collection id to use for 'filter' endpoint.
 * @param {Number} limit - Number of products to retrieve per page.
 * @param {Number} page - Current page number.
 * @param {String} query - Search query.
 * @param {String} storageKey - Storage key used for temp collection storage.
 *
 */
import { formatNostoProduct, formatShopifyPrices } from '~/helpers/format-nosto-api'

import availableQuery from '~/graphql/queries/product-available.gql'
import pricesQuery from '~/graphql/queries/product-prices.gql'

import { waitFor } from '~/helpers/general'

export default {
  name: 'NostoApiCall',

  props: {
    choices: {
      type: String,
      default: '',
    },
    collectionId: {
      type: Number,
      default: null,
    },
    isLoadMoreClicked: {
      type: Boolean,
      default: false,
    },
    limit: {
      type: Number,
      default: 16,
    },
    query: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: '',
    },
    storageKey: {
      type: String,
      default: 'collection-temp',
    },
  },

  emits: {
    'colours-updated': (payload) => typeof payload === 'object',
    'nosto-updated': (payload) => typeof payload === 'object',
    'prices-updated': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      global: true,
      sessionCookie: false,
      usedPrefetchedData: false,
      usedCachedData: false,
    }
  },

  computed: {

    /**
     * Get cache key for current collection/search.
     * @returns {String}
     */
    cacheKey() {
      if (this.type === 'collection') {
        return `cache-to-collection-${this.collectionId}`
      }

      return null
    },

    /**
     * Get cache duration in milliseconds from theme settings.
     * @returns {Number}
     */
    cacheDurationMs() {
      const minutes = window.cnvs?.settings?.nostoCacheDurationMinutes ?? 60
      return minutes * 60 * 1000
    },

    /**
     * Check if caching is enabled.
     * @returns {Boolean}
     */
    isCachingEnabled() {
      return this.cacheDurationMs > 0
    },
  },

  watch: {

    /**
     * Watch choices update to get product results.
     * - Ignore update if products and choices are being set from storage.
     */
    choices(choices) {
      if (sessionStorage.getItem(this.storageKey)) {
        sessionStorage.removeItem(this.storageKey)
        return
      }

      this.getProductResults(choices)
    },

    /**
     * Watch query update to get product results.
     */
    query() {
      if (this.type !== 'autocomplete') {
        return
      }

      this.getProductResults(this.choices)
    },
  },

  beforeMount() {
    this.setSessionCookieData('nostoSearchSessionParams')

    this.$nextTick().then(() => {
      if (!this.sessionCookie) {
        this.setNostoSession()
      }
    })
  },

  methods: {

    /**
     * Generate cache key for specific choices/page combination.
     * @param {String} choices - Filter choices string.
     * @returns {String}
     */
    getCacheKeyForChoices(choices) {
      const baseKey = this.cacheKey

      if (!baseKey) {
        return null
      }

      // Include choices in cache key for filtered results
      const choicesHash = choices ? btoa(choices).slice(0, 20) : 'default'
      return `${baseKey}-${choicesHash}`
    },

    /**
     * Check if cached data is valid and not expired.
     * @param {String} choices - Filter choices string.
     * @returns {Object|null} - Cached data or null if invalid/expired
     */
    getCachedData(choices) {
      if (!this.isCachingEnabled) {
        return null
      }

      const cacheKey = this.getCacheKeyForChoices(choices)

      if (!cacheKey) {
        return null
      }

      try {
        const cached = sessionStorage.getItem(cacheKey)

        if (!cached) {
          return null
        }

        const parsedCache = JSON.parse(cached)

        // Check if cache has expired
        if (Date.now() - parsedCache.timestamp > this.cacheDurationMs) {
          sessionStorage.removeItem(cacheKey)
          return null
        }

        // Validate cache structure
        if (!parsedCache.data || !parsedCache.collectionId) {
          sessionStorage.removeItem(cacheKey)
          return null
        }

        // Ensure collection ID matches
        if (this.type === 'collection' && parsedCache.collectionId !== String(this.collectionId)) {
          return null
        }

        return parsedCache

      } catch (error) {
        // Clear corrupted cache
        sessionStorage.removeItem(cacheKey)
        return null
      }
    },

    /**
     * Save response data to cache.
     * @param {String} choices - Filter choices string.
     * @param {Object} response - Nosto API response.
     * @param {Array} formattedProducts - Formatted product data.
     * @param {Number} page - Current page number.
     */
    setCachedData(choices, response, formattedProducts, page) {
      if (!this.isCachingEnabled) {
        return
      }

      const cacheKey = this.getCacheKeyForChoices(choices)

      if (!cacheKey) {
        return
      }

      try {
        const cacheData = {
          timestamp: Date.now(),
          collectionId: String(this.collectionId),
          query: this.query,
          data: response,
          formattedProducts,
          page,
        }

        sessionStorage.setItem(cacheKey, JSON.stringify(cacheData))

      } catch (error) {
        // Handle quota exceeded or other storage errors
        this.cleanupOldCache()

        try {
          const cacheData = {
            timestamp: Date.now(),
            collectionId: String(this.collectionId),
            query: this.query,
            data: response,
            formattedProducts,
            page,
          }

          sessionStorage.setItem(cacheKey, JSON.stringify(cacheData))
        } catch {
          window.console.warn('Unable to cache Nosto response', error)
        }
      }
    },

    /**
     * Clean up old/expired Nosto cache entries.
     */
    cleanupOldCache() {
      const keysToRemove = []

      Object.keys(sessionStorage).forEach((key) => {
        if (key && key.startsWith('cache-to-')) {
          try {
            const cached = JSON.parse(sessionStorage.getItem(key))

            if (Date.now() - cached.timestamp > this.cacheDurationMs) {
              keysToRemove.push(key)
            }

          } catch {
            keysToRemove.push(key)
          }
        }
      })

      keysToRemove.forEach((key) => sessionStorage.removeItem(key))
    },

    /**
     * Use cached data instead of making API call.
     * @param {Object} cached - Cached data object.
     */
    useCachedData(cached) {
      this.usedCachedData = true

      this.$emit('nosto-updated', {
        ...cached.data.products,
        formattedProducts: cached.formattedProducts,
        suggestions: cached.data?.keywords?.hits,
        action: 'replace',
        page: cached.page || 1,
        fromCache: true,
      })

      if (cached.formattedProducts.length) {
        this.queryShopifyPrices(cached.formattedProducts).then(prices => {
          this.$emit('prices-updated', prices)
        })

        this.queryColourHandles(cached.formattedProducts).then(colours => {
          this.$emit('colours-updated', colours)
        })
      }
    },

    /**
     * Checks if we can use prefetched data from the web component.
     * @returns {Boolean} - True if prefetched data can be used
     */
    canUsePrefetchedData() {
      const prefetch = window.cnvsPrefetchedNosto

      if (!prefetch || prefetch.status !== 'complete' || !prefetch.data) {
        return false
      }

      if (this.usedPrefetchedData) {
        return false
      }

      if (prefetch.collectionId !== String(this.collectionId)) {
        return false
      }

      if (this.type !== 'collection') {
        return false
      }

      if (this.choices && this.choices.length > 0) {
        return false
      }

      const maxAge = this.$variable('settings.nostoCacheDurationMinutes') * 60 * 1000

      if (Date.now() - prefetch.timestamp > maxAge) {
        window.cnvsPrefetchedNosto = false
        return false
      }

      return true
    },

    /**
     * Uses the prefetched data instead of making a new API call.
     * This is called when canUsePrefetchedData() returns true.
     */
    usePrefetchedData() {
      const prefetch = window.cnvsPrefetchedNosto
      const response = prefetch.data

      this.usedPrefetchedData = true

      window.cnvsPrefetchedNosto = null
      const formattedProducts = response.products.hits.map(formatNostoProduct)

      this.$emit('nosto-updated', {
        ...response.products,
        formattedProducts,
        suggestions: response?.keywords?.hits,
        action: 'replace',
        page: 1,
      })

      if (formattedProducts.length) {
        this.queryShopifyPrices(formattedProducts).then(prices => {
          this.$emit('prices-updated', prices)
        })

        this.queryColourHandles(formattedProducts).then(colours => {
          this.$emit('colours-updated', colours)
        })
      }
    },

    /**
     * Waits for prefetched data if it's currently loading.
     * Returns a promise that resolves when data is ready or times out.
     * @returns {Promise<Boolean>} - True if prefetch data is available
     */
    waitForPrefetchedData() {
      return new Promise((resolve) => {
        const prefetch = window.cnvsPrefetchedNosto

        if (!prefetch || prefetch.status !== 'loading') {
          resolve(this.canUsePrefetchedData())
          return
        }

        const maxWait = 5000
        const pollRate = 50
        const startTime = performance.now()

        const intervalId = setInterval(() => {
          const currentPrefetch = window.cnvsPrefetchedNosto

          if (currentPrefetch && currentPrefetch.status === 'complete') {
            clearInterval(intervalId)
            resolve(this.canUsePrefetchedData())
            return
          }

          const elapsed = performance.now() - startTime
          if (!currentPrefetch || currentPrefetch.status === 'error' || elapsed >= maxWait) {
            clearInterval(intervalId)
            resolve(false)
          }
        }, pollRate)
      })
    },

    /**
     * Handle API call to Nosto.
     * @param {String} choices - Search parameters to add to request url.
     */
    async getProductResults(choices) {
      performance.mark('cnvs.nosto.start')

      // Check for prefetched data first (initial load only)
      if (!this.usedPrefetchedData && this.type === 'collection') {
        const canUsePrefetch = await this.waitForPrefetchedData()

        if (canUsePrefetch) {
          this.usePrefetchedData()
          return
        }
      }

      // Check for cached data (repeat visits)
      const cachedData = this.getCachedData(choices)

      if (cachedData && !this.isLoadMoreClicked) {
        this.useCachedData(cachedData)
        return
      }

      if (!this.sessionCookie) {
        return
      }

      try {

        /**
         * Calculate new page to load to get action.
         */
        const params = new URLSearchParams(choices)
        let searchType = 'serp'
        let newPage = 1

        if (params.get('page')) {
          newPage = Number(params.get('page'))
        }

        const filters = []

        const filterParams = choices
          .split('&')
          .filter(param => !param.startsWith('page=') && param.includes('='))

        /**
         * Convert raw parameters into Nosto-compatible filters.
         */
        for (const param of filterParams) {
          const [rawKey, rawValue] = param.split('=')

          const field = rawKey
            .replace('pf_', '')
            .replace('%5B%5D', '')
            .replace('%5b%5d', '')
            .replace('[]', '')
            .replaceAll('__', '.')

          const value = decodeURIComponent(rawValue || '')

          if (!field || field === 'sort') {
            continue
          }

          if (field === 'listPrice') {
            const splitCharacter = value.includes('%3a')
              ? '%3a'
              : '%3A'

            const [gt, lt] = value.split(splitCharacter)

            filters.push({
              field,
              range: { gt, lt },
            })

            continue
          }

          const existingFilter = filters.find((filter) => filter.field === field)

          if (existingFilter) {
            existingFilter.value.push(value)

          } else {
            filters.push({
              field,
              value: [value],
            })
          }
        }

        /**
         * Get sort by option.
         */
        let sortBy = false
        let sortOrder = 'asc'
        const sortingParam = params.get('sort')

        if (sortingParam) {
          if (sortingParam.includes('descending')) {
            sortOrder = 'desc'
            sortBy = sortingParam.replace('-descending', '')
          } else {
            sortOrder = 'asc'
            sortBy = sortingParam.replace('-ascending', '')
          }
        }

        /**
         * Construct product data.
         */
        const productData = {
          size: this.limit,
          from: newPage === 1 ? 0 : this.limit * (newPage - 1),
          facets: ['*'],
          fields: [
            'available',
            'priceCurrencyCode',
            'listPrice',
            'price',
            'productId',
            'url',
            'name',
            'tags1',
            'tags2',
            'tags3',
            'alternateImageUrls',
            'description',
            'imageUrl',
            'brand',
            'customFields.key',
            'customFields.value',
            'skus.id',
            'skus.name',
            'skus.listPrice',
            'skus.price',
            'skus.availability',
            'skus.customFields.key',
            'skus.customFields.value',
          ],
        }

        if (this.type === 'collection') {
          productData.categoryId = this.collectionId?.toString() || ''
          searchType = 'category'
        } else if (this.type === 'autocomplete') {
          searchType = 'autocomplete'
        }

        if (filters.length > 0) {
          productData.filter = filters
        }

        if (sortBy) {
          productData.sort = [
            {
              field: sortBy.replace('price', 'customFields.custom-min_price'),
              order: sortOrder,
            },
          ]
        }

        const searchParams = {
          products: productData,
          keywords: {
            fields: ['keyword'],
            size: 5,
          },
          sessionParams: JSON.parse(decodeURIComponent(this.sessionCookie)),
        }

        if (this.query) {
          searchParams.query = decodeURIComponent(this.query)
        }

        performance.mark('cnvs.nosto.response.start')

        /**
         * Query data.
         */
        const callNosto = () => {
          nostojs(api => {
            api.search(searchParams, {
              redirect: searchType !== 'autocomplete',
              track: searchType,
            }).then(async(response) => {

              /**
               * Format data.
               */
              const formattedProducts = response.products.hits.map(formatNostoProduct)

              /**
               * Cache the response for future page loads.
               * Only cache first page results without load more.
               */
              if (!this.isLoadMoreClicked && newPage === 1) {
                this.setCachedData(choices, response, formattedProducts, newPage)
              }

              /**
               * Emit event.
               * - Do not render prices.
               */
              this.$emit('nosto-updated', {
                ...response.products,
                formattedProducts,
                suggestions: response?.keywords?.hits,
                action: this.isLoadMoreClicked ? 'append' : 'replace',
                page: newPage,
              })

              /**
               * Emit separate event with update prices and colours.
               */
              if (formattedProducts.length) {
                const prices = await this.queryShopifyPrices(formattedProducts)
                this.$emit('prices-updated', prices)

                const colours = await this.queryColourHandles(formattedProducts)
                this.$emit('colours-updated', colours)
              }

              performance.mark('cnvs.nosto.response.end')

              performance.measure(
                'cnvs.nosto.response',
                'cnvs.nosto.response.start',
                'cnvs.nosto.response.end',
              )
            })
          })
        }

        if (window.nostojs) {
          callNosto()
        } else {
          waitFor('nostojs', callNosto)
        }

      } catch (error) {
        throw new Error(error)
      }
    },

    /**
     * Query Shopify prices.
     * - An extra network call was required to enable market pricing and to
     *   query availability of colour swatch products.
     * @param {Object} products - Formatted product data.
     * @returns {Promise<Array>}
     */
    queryShopifyPrices(products) {
      return new Promise(async(resolve, reject) => {
        const query = products
          .map((product) => `id:${product.productId}`)
          .join(' OR ')

        try {
          const response = await cnvs.Query({
            query: pricesQuery,
            variables: {
              country: cnvs.store.country,
              language: cnvs.store.language,
              query,
            },
          })

          if (!response.products?.edges?.length) {
            resolve([])
            return
          }

          const prices = response.products.edges?.map(formatShopifyPrices)
          resolve(prices)

        } catch (error) {
          cnvs.ReportError('Failed to load product prices', error)
          reject(error)
        }
      })
    },

    /**
     * Query Shopify colour handles.
     * @param {Array} products - Formatted product data.
     * @returns {Promise<Object>}
     */
    queryColourHandles(products) {
      return new Promise(async(resolve, reject) => {
        try {

          /**
           * Build array of unique colour handles.
           */
          let colourHandles = products
            .map((product) => {
              if (product.coloursLoaded) {
                return false
              }

              return Object.values(product.colours)
            })
            .flat(1)
            .filter(Boolean)

          const uniqueHandles = []

          colourHandles = colourHandles.filter((handle) => {
            if (uniqueHandles.includes(handle)) {
              return false
            }

            uniqueHandles.push(handle)
            return true
          })

          /**
           * Query each colour handle.
           * - Can't use nodes as that requires IDs and the metafield is a JSON
           *   object containing handles only.
           */
          const queue = colourHandles.map((handle) => {
            return cnvs.Query({
              query: availableQuery,
              variables: {
                country: cnvs.store.country,
                handle,
                language: cnvs.store.language,
              },
            })
          })

          const response = await Promise.all(queue)

          /**
           * Combine response in single object with boolean values.
           */
          const colours = {}

          response.forEach(({ product }, index) => {
            const handle = colourHandles[index]
            colours[handle] = product?.availableForSale ?? false
          })

          resolve(colours)

        } catch (error) {
          cnvs.ReportError('Failed to load colour availability', error)
          reject(error)
        }
      })
    },

    /**
     * Set Nosto session.
     */
    setNostoSession() {
      const callNosto = () => {
        nostojs(api => {
          api.getSearchSessionParams().then(response => {
            this.sessionCookie = encodeURIComponent(JSON.stringify(response))
            document.cookie = `nostoSearchSessionParams=${this.sessionCookie}`

            this.$nextTick().then(() => {
              this.getProductResults(this.choices)
            })
          })
        })
      }

      if (window.nostojs) {
        callNosto()
      } else {
        waitFor('nostojs', callNosto)
      }
    },

    /**
     * Set session cookie data.
     */
    setSessionCookieData(name) {
      const value = `; ${document.cookie}`
      const parts = value.split(`; ${name}=`)

      if (parts.length === 2) {
        this.sessionCookie = parts.pop().split(';').shift()

        return
      }

      this.sessionCookie = false
    },
  },
}
</script>
